import { memo, useState } from 'react'
import { AlertTriangle, Home, User, Mail, CheckCircle, XCircle } from 'lucide-react'
import { useApp } from '../context/useApp'

export default memo(function TransferRequestModal() {
  const { transferRequests, handleTransfer } = useApp()
  const [processing, setProcessing] = useState(null) // requestId currently being processed
  const [error, setError] = useState(null)
  const [confirming, setConfirming] = useState(null) // 'reject' requestId — requires confirmation step

  if (!transferRequests || transferRequests.length === 0) return null

  const current = transferRequests[0]

  async function act(action) {
    if (action === 'reject' && confirming !== current._id) {
      setConfirming(current._id)
      return
    }
    setError(null)
    setProcessing(current._id)
    setConfirming(null)
    try {
      await handleTransfer(current.propertyId, current._id, action)
    } catch {
      setError('Something went wrong. Please try again.')
      setProcessing(null)
    }
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="bg-navy-card border border-gold/30 rounded-2xl shadow-2xl w-full max-w-md">

        {/* Header */}
        <div className="flex items-center gap-3 px-6 pt-6 pb-4 border-b border-slate-border">
          <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <h2 className="text-cream font-semibold text-base leading-tight">
              Ownership Transfer Request
            </h2>
            <p className="text-parchment/50 text-xs mt-0.5">
              Action required — this cannot be dismissed
            </p>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          <p className="text-parchment/80 text-sm leading-relaxed">
            Someone has submitted verified ownership documents for one of your properties.
            Please review and respond.
          </p>

          {/* Property */}
          <div className="bg-navy/50 border border-slate-border rounded-xl p-4 space-y-2">
            <div className="flex items-center gap-2 text-sm text-cream font-medium">
              <Home className="w-4 h-4 text-gold flex-shrink-0" />
              {current.propertyAddress}
            </div>
            <div className="flex items-center gap-2 text-xs text-parchment/60">
              <User className="w-3.5 h-3.5 text-sepia flex-shrink-0" />
              {current.requesterName}
            </div>
            {current.requesterEmail && (
              <div className="flex items-center gap-2 text-xs text-parchment/60">
                <Mail className="w-3.5 h-3.5 text-sepia flex-shrink-0" />
                {current.requesterEmail}
              </div>
            )}
          </div>

          {transferRequests.length > 1 && (
            <p className="text-xs text-parchment/40 text-center">
              {transferRequests.length - 1} more request{transferRequests.length > 2 ? 's' : ''} pending after this one
            </p>
          )}

          {error && (
            <p className="text-xs text-red-400 text-center">{error}</p>
          )}

          {/* Rejection confirmation step */}
          {confirming === current._id && (
            <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-4 text-sm text-parchment/80">
              <p className="font-medium text-red-400 mb-1">Confirm rejection</p>
              <p className="text-xs text-parchment/60">
                Rejecting will flag this as a disputed transfer. An admin will review why someone
                else could verify ownership of your property. This cannot be undone.
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="px-6 pb-6 flex gap-3">
          <button
            onClick={() => act('reject')}
            disabled={!!processing}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-transparent border border-red-500/40 text-red-400 hover:bg-red-500/10 text-sm font-medium cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <XCircle className="w-4 h-4" />
            {confirming === current._id ? 'Confirm Reject' : 'Reject'}
          </button>
          <button
            onClick={() => act('approve')}
            disabled={!!processing}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-gold hover:bg-gold-light text-white text-sm font-medium cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <CheckCircle className="w-4 h-4" />
            {processing === current._id ? 'Processing…' : 'Approve Transfer'}
          </button>
        </div>

      </div>
    </div>
  )
})
